/**
 * @file: The text inside one bar of the grid, and its tooltip.
 *
 * The height of a bar in pixels selects what the bar can hold. The height
 * comes from the minutes of the bar and the zoom on screen. The functions here
 * are pure, so a test can call them without FullCalendar.
 */

import moment from 'moment'

import { escapeHtml, safeCssColor } from 'common/dom-util'
import { labelForRole } from 'common/interval-roles'
import { formatRange, MINUTES_IN_DAY, RANGE_DASH } from 'common/date-time'

/**
 * Make one line with the name, the full times and the role.
 *
 * The line becomes the tooltip of the bar. A bar too small for text therefore
 * still shows this data on hover. The never-on-screen mark is a glyph only, so
 * the line also gives its words.
 *
 * @param {Object} event - A FullCalendar event
 * @param {Object} [marks] - { neverOnScreen } when the bar has that mark
 * @returns {string}
 */
export const describeEvent = (event, { neverOnScreen = false } = {}) => {
  const role = labelForRole(event.extendedProps && event.extendedProps.role)
  const warning = neverOnScreen ? 'Never on screen' : null

  return [event.title, formatRange(event.start, event.end), role, warning].filter(Boolean).join(' · ')
}

/**
 * The height of one slot row in pixels. The calendar manager writes it to the
 * grid as a CSS variable, and the LESS file reads that variable. The shape of
 * a bar and the drawn rows then use one value.
 */
export const SLOT_ROW_PX = 50

/**
 * The height in pixels of a bar of this many minutes at the current zoom.
 *
 * @param {number} minutes - The length of the bar
 * @param {number} slotMinutes - The length of one slot row at the current zoom
 * @returns {number}
 */
function barHeightPx (minutes, slotMinutes) {
  return minutes / slotMinutes * SLOT_ROW_PX
}

/**
 * Does the bar for an interval fill at most one slot row?
 *
 * A row is SLOT_ROW_PX high. A bar with the height of one row or less has no
 * space for two lines of text with the full decoration. Such a bar therefore
 * gets the small decoration.
 *
 * The test compares the interval with the row, and not with a fixed number of
 * minutes. A minute has a different height in pixels at each zoom level. With
 * two-hour rows, one hour is 25px. With quarter-hour rows, one hour is 200px.
 * A limit of 60 minutes is correct only for one-hour rows. At the widest zoom,
 * a bar of 72 to 122 minutes then kept the full decoration. The bar was too
 * small for that decoration, so the title line did not show.
 *
 * @param {number} durationMinutes - The length of the interval
 * @param {number} slotMinutes - The length of one slot row at the current zoom
 * @returns {boolean}
 */
export const fillsAtMostOneRow = (durationMinutes, slotMinutes) =>
  Number.isFinite(durationMinutes) && Number.isFinite(slotMinutes) &&
  slotMinutes > 0 && barHeightPx(durationMinutes, slotMinutes) <= SLOT_ROW_PX

/**
 * Below this height a bar holds the name only. FullCalendar uses the same
 * 30px for its own short bars.
 */
const ONE_LINE_PX = 30

/** From this height a bar holds the time line above the name. */
const TWO_LINES_PX = 45

export const BAR_SHAPES = Object.freeze({
  name: 'name',
  line: 'line',
  stack: 'stack'
})

/**
 * Choose the content of a bar from its height in pixels.
 *
 * The height comes from the minutes and the zoom. With two-hour rows one hour
 * is 25px, and with quarter-hour rows it is 200px. A choice from the minutes
 * alone hid the name at one zoom and the time at another.
 *
 * @param {number} minutes - The length of the bar, not of the whole interval
 * @param {number} slotMinutes - The length of one slot row at the current zoom
 * @returns {string} A value of BAR_SHAPES
 */
export function barShape (minutes, slotMinutes) {
  const height = barHeightPx(minutes, slotMinutes)

  if (!(slotMinutes > 0) || !(height >= ONE_LINE_PX)) {
    return BAR_SHAPES.name
  }

  return height < TWO_LINES_PX ? BAR_SHAPES.line : BAR_SHAPES.stack
}

/**
 * The minutes of the one bar that FullCalendar draws in one day column.
 *
 * An interval over midnight has one bar for each day. The bar on the first day
 * ends at midnight, and the bar on the last day starts at midnight.
 *
 * @param {Object} arg - The eventContent argument of FullCalendar
 * @returns {number}
 */
export function barMinutes ({ event, isStart, isEnd }) {
  const start = moment(event.start)
  const end = moment(event.end)

  if (isStart === false && isEnd === false) {
    return MINUTES_IN_DAY
  }

  const from = isStart === false ? end.clone().startOf('day') : start
  const to = isEnd === false ? start.clone().add(1, 'day').startOf('day') : end

  return to.diff(from, 'minutes')
}

/**
 * Make the content of one bar.
 *
 * The height of the bar selects one of three shapes. A low bar shows the name
 * only, because its position in the grid gives the time. A middle bar shows the
 * start time and the name on one line. A tall bar shows the time line above
 * the name.
 *
 * The start and the end are two spans. A narrow bar moves the end to a new
 * line, or cuts it with an ellipsis. It never shows half of the end time,
 * because "12:30–13" for 13:45 reads as the end 13:00.
 *
 * The text has a background in the bar colour. A hatch on the bar is then
 * never visible behind the text.
 *
 * Every value goes into raw HTML or into a title attribute, and a playlist
 * name is text an operator wrote, so each one is escaped. escapeHtml escapes
 * quotes as well, which is what makes the attribute safe.
 *
 * @param {Object} arg - The eventContent argument of FullCalendar
 * @param {Object} bar - { slotMinutes } at the current zoom, and
 *   { neverOnScreen } when the bar has that mark
 * @returns {Object} { html }
 */
export function barContent (arg, { slotMinutes, neverOnScreen = false }) {
  const shape = barShape(barMinutes(arg), slotMinutes)
  const [start, end] = String(arg.timeText || '').split(RANGE_DASH)
  const textBackground = safeCssColor(arg.backgroundColor)
  const textStyle = textBackground ? ` style="background-color: ${textBackground}"` : ''

  const title = `<span class="schedule-bar-title">${escapeHtml(arg.event.title)}</span>`
  const startTime = start ? `<span class="schedule-bar-start">${escapeHtml(start)}</span>` : ''
  const endTime = end ? `<span class="schedule-bar-end">${RANGE_DASH}${escapeHtml(end)}</span>` : ''

  const lines = {
    [BAR_SHAPES.name]: title,
    [BAR_SHAPES.line]: `${startTime}${title}`,
    [BAR_SHAPES.stack]: `<span class="schedule-bar-time">${startTime}${endTime}</span>${title}`
  }

  return {
    html: `<div class="schedule-bar is-${shape}" title="${escapeHtml(describeEvent(arg.event, { neverOnScreen }))}">` +
      `<span class="schedule-bar-text"${textStyle}>${lines[shape]}</span></div>`
  }
}
